import {
  keepPreviousData,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { ENDPOINT } from '~/constants';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysFtpPoller } from './queryKeys';
import { type FtpPollersResponse, type GetFtpPollersPayload } from './types';
import {
  zodFtpPollersQueryParamsSchema,
  zodFtpPollersResponseSchema,
} from './validations';

/**
 * Fetches all FTP pollers from the API.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-FTP-Secrets/operation/get_all_ftp_secrets_vadmin_ftp_poller_secret_get
 */
export const fetchFtpPollers = async (searchParams: GetFtpPollersPayload) => {
  try {
    return await vestigasApi
      .get(ENDPOINT.FTP_POLLER.GET_ALL(), {
        searchParams: searchParams as Record<
          string,
          string | number | boolean | undefined
        >,
        validate: {
          isResponseSensitive: true,
          response: zodFtpPollersResponseSchema,
          searchParams: zodFtpPollersQueryParamsSchema,
        },
      })
      .json<FtpPollersResponse>();
  } catch (error) {
    log.error({
      message: 'Error fetching FTP pollers',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * Generates FTP-poller query options shared by the list hook and route loader.
 *
 * @param queryParameters - List filters, pagination, and server-side sort settings.
 * @param options - Additional React Query options.
 * @returns Query options for the FTP poller list.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-FTP-Secrets/operation/get_all_ftp_secrets_vadmin_ftp_poller_secret_get
 */
export const getFtpPollersQueryOptions = ({
  options = {},
  queryParameters,
}: {
  options?: Omit<UseQueryOptions<FtpPollersResponse>, 'queryKey' | 'queryFn'>;
  queryParameters: Partial<GetFtpPollersPayload>;
}): UseQueryOptions<FtpPollersResponse> => {
  const validatedSearchParams =
    zodFtpPollersQueryParamsSchema.parse(queryParameters);

  return {
    queryFn: async () => fetchFtpPollers(validatedSearchParams),
    queryKey: queryKeysFtpPoller.getAll(validatedSearchParams),
    staleTime: ms('30s'),
    ...options,
  };
};

/**
 * Gets paginated FTP pollers while retaining the previous page during a page change.
 *
 * @param queryParameters - List filters, pagination, and server-side sort settings.
 * @param options - Additional React Query options.
 * @returns The FTP poller list query result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-FTP-Secrets/operation/get_all_ftp_secrets_vadmin_ftp_poller_secret_get
 */
export const useQueryFtpPollers = (
  queryParameters: Partial<GetFtpPollersPayload>,
  options?: Omit<UseQueryOptions<FtpPollersResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<FtpPollersResponse>({
    ...getFtpPollersQueryOptions({
      options,
      queryParameters,
    }),
    placeholderData: keepPreviousData,
  });
};
